// Shared helpers for the screens in main.js: time windows, formatting, the ticket-odds card, the
// 24-hour strip, the posted sign, sheets and the pinned action bar. No screen state lives here.

import { prefs } from './store.js'
import { local, fromLocal, addDays, when, clock, span, timesLabel, MIN, HOUR } from '../src/shared/time.ts'
import { CAT_LABEL } from '../src/shared/risk.ts'

export const $ = (id) => document.getElementById(id)
export const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c])
export const plural = (n, one, many = one + 's') => `${n} ${n === 1 ? one : many}`
export const andList = (xs) => (xs.length < 3 ? xs.join(' and ') : `${xs.slice(0, -1).join(', ')} and ${xs.at(-1)}`)
export const money = (cents) => `$${(cents / 100).toFixed(2)}`
/** A spot's colour: a meter that's free for your whole stay shows as free (green), like its curb line. */
export const look = (c) => (c.kind === 'meter' && c.cost === 0 ? 'free' : c.kind)
export const walkMin = (m) => Math.max(1, Math.round(m / 80))
export const SIDE = { N: 'north side', S: 'south side', E: 'east side', W: 'west side' }

// ---------------------------------------------------------------- time windows

const DURS = [
  [30, '30 min'], [60, '1 hr'], [120, '2 hr'], [180, '3 hr'], [240, '4 hr'], ['night', 'Overnight'],
]

export function windowFor(dur, start = Date.now()) {
  if (dur === 'night') {
    const l = local(start)
    const date = l.min < 5 * 60 ? l.date : addDays(l.date, 1)
    return { start, end: Math.max(fromLocal(date, 8 * 60), start + 30 * MIN) }
  }
  // A one-off stay with a fixed end ("Plan until 2:30am"): it can't roll over to tomorrow.
  if (typeof dur === 'string' && dur.startsWith('end:')) return { start, end: Math.max(start + 10 * MIN, Number(dur.slice(4))) }
  if (typeof dur === 'string' && dur.startsWith('until:')) {
    const m = Number(dur.slice(6))
    const l = local(start)
    let end = fromLocal(l.date, m)
    if (end < start + 15 * MIN) end = fromLocal(addDays(l.date, 1), m)
    return { start, end }
  }
  return { start, end: start + Number(dur) * MIN }
}

export function durLabel(dur) {
  const hit = DURS.find(([d]) => d === dur)
  if (hit) return hit[1]
  if (typeof dur === 'string' && dur.startsWith('until:')) return `Until ${clock(Number(dur.slice(6)))}`
  if (typeof dur === 'string' && dur.startsWith('end:')) return `Until ${clock(local(Number(dur.slice(4))).min)}`
  return span(Number(dur) * MIN)
}

export function durChips(cur, name = 'dur') {
  if (typeof cur === 'string' && cur.startsWith('end:')) cur = `until:${local(Number(cur.slice(4))).min}`
  const custom = typeof cur === 'string' && cur.startsWith('until:')
  return `<div class="chips" role="radiogroup" aria-label="How long">
    ${DURS.map(([d, label]) => `<button type="button" class="chip-btn" role="radio" aria-checked="${d === cur}" data-${name}="${d}">${label}</button>`).join('')}
    <label class="chip-btn until" data-on="${custom}"><span>${custom ? esc(durLabel(cur)) : 'Until…'}</span><input type="time" data-until step="900" value="${custom ? hhmm(Number(cur.slice(6))) : ''}" aria-label="Until a time"></label>
  </div>`
}
const hhmm = (m) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`

export function wireChips(root, onPick, name = 'dur') {
  root.querySelectorAll(`[data-${name}]`).forEach((b) => (b.onclick = () => {
    const v = b.dataset[name]
    onPick(v === 'night' ? 'night' : Number(v))
  }))
  const t = root.querySelector('[data-until]')
  if (t) t.onchange = () => {
    if (!t.value) return
    const [h, m] = t.value.split(':').map(Number)
    onPick(`until:${h * 60 + m}`)
  }
}

// ---------------------------------------------------------------- shared bits

export const NAV_APPS = { apple: 'Apple Maps', google: 'Google Maps', waze: 'Waze', tesla: 'Tesla' }

// Apple's unified Maps URLs (maps.apple.com/directions, with waypoints) arrived in iOS 18.4, and the
// old ?daddr= links stopped working reliably after it. Older iPhones keep the old form.
const oldIos = () => { const m = /(?:iPhone|CPU) OS (\d+)_(\d+)/.exec(navigator.userAgent); return !!m && (Number(m[1]) < 18 || (Number(m[1]) === 18 && Number(m[2]) < 4)) }
const appleish = () => /iPhone|iPad|Macintosh/.test(navigator.userAgent)
const ll = (p) => `${p.lat},${p.lon}`

function appleUrl(p, mode) {
  if (oldIos()) return `https://maps.apple.com/?daddr=${ll(p)}&dirflg=${mode === 'walking' ? 'w' : 'd'}`
  return `https://maps.apple.com/directions?destination=${ll(p)}&mode=${mode}`
}
const googleUrl = (p, mode) => `https://www.google.com/maps/dir/?api=1&destination=${ll(p)}&travelmode=${mode}`

export function navUrl(p, mode = 'driving') {
  const nav = prefs().nav
  // Waze has no walking mode; walking back to the car goes to the phone's own maps. Tesla gets the
  // spot through the share sheet (goAttrs); this link is for when sharing isn't there.
  if (nav === 'waze' && mode === 'driving') return `https://waze.com/ul?ll=${ll(p)}&navigate=yes`
  if (nav === 'apple' || ((nav === 'waze' || nav === 'tesla') && appleish())) return appleUrl(p, mode)
  return googleUrl(p, mode)
}

/** A driving "Navigate" link's attributes: the map link, plus the spot for "Send to Tesla" (main.js). */
export const goAttrs = (p, name = p.street || p.name || '') => `href="${navUrl(p)}" target="_blank" rel="noopener" data-go="${ll(p)}" data-go-name="${esc(name)}"`

/**
 * One route past every spot, ending at the garage if they're all taken: Apple Maps (iOS 18.4+) or
 * Google Maps, whichever you use; Waze and Tesla take one destination at a time, so they get the
 * phone's maps app. Google's apps take 9 waypoints (its mobile website only 3). Returns [url, app].
 */
export function loopUrl(spots, garage) {
  if (!spots.length) return null
  const stops = garage ? [...spots, garage] : [...spots]
  const dest = stops[stops.length - 1], way = stops.slice(0, -1)
  const nav = prefs().nav
  const apple = (nav === 'apple' || (nav !== 'google' && appleish())) && !oldIos()
  if (apple) return [`https://maps.apple.com/directions?destination=${ll(dest)}${way.map((w) => `&waypoint=${ll(w)}`).join('')}&mode=driving`, 'Apple Maps']
  const g = way.slice(-9)
  return [`https://www.google.com/maps/dir/?api=1&destination=${ll(dest)}&travelmode=driving${g.length ? `&waypoints=${g.map(ll).join('%7C')}` : ''}`, 'Google Maps']
}

/** What "Send to Tesla" shares: the Tesla app takes a maps link or an address from the share sheet. */
export function shareSpot(lat, lon, name) {
  const url = appleish() ? `https://maps.apple.com/?ll=${lat},${lon}&q=${encodeURIComponent(name || 'Parking spot')}` : `https://www.google.com/maps/search/?api=1&query=${lat},${lon}`
  return { title: name || 'Parking spot', text: name ? `${name}, Los Angeles` : '', url }
}

export const sideText = (c) => (c.side ? SIDE[c.side] : '')

const ENFORCER = { SM: 'Santa Monica', BH: 'Beverly Hills', LB: 'Long Beach', TOR: 'Torrance', GAR: 'Gardena', ING: 'Inglewood', ES: 'El Segundo', PAS: 'Pasadena' }

/**
 * Big venues nearby: the game that overlaps the stay, or that the schedules show none. Concerts and
 * the Bowl's shows aren't in any feed, so those venues keep the general warning.
 */
/** "W Sunset Blvd → Selma Ave" reads like a route: "between W Sunset Blvd and Selma Ave". */
export const crossText = (cross) => {
  const m = /^(.+?) → (.+)$/.exec(cross || '')
  return m ? `between ${m[1]} and ${m[2]}` : cross || ''
}

export function eventNote(ev, now = Date.now()) {
  if (!ev?.venues?.length) return ''
  const g = ev.games?.[0]
  if (g) {
    // Always say the day: "11am" alone, the evening before, reads as this morning.
    // An untimed game is dated midnight Eastern or Pacific; six hours on lands on its day either way.
    const day = dayWord(g.timed ? g.at : g.at + 6 * HOUR, now)
    const time = g.timed ? `${clock(local(g.at).min)} ${day}` : `${day}, time not set yet`
    const more = ev.games.length > 1 ? `, and ${ev.games.length === 2 ? 'another game' : `${ev.games.length - 1} more games`} during your stay` : ''
    return `<p class="warn"><b>${esc(g.name)}</b>, ${esc(time)} at ${esc(g.place || g.venue)}${more}. Expect temporary no-parking and tow-away signs on the streets around it, so read every sign.</p>`
  }
  // Outside 2pm-midnight there's no show to warn about. Games are in the schedules, so a stadium we
  // couldn't read lately still gets the warning (1pm kickoffs).
  const quiet = ev.evening === false
  const listed = ev.venues.filter((v) => v.listed)
  if (listed.length === ev.venues.length) {
    if (quiet) return ''
    const who = listed.length === 1 && listed[0].teams ? `${listed[0].teams} game at ${listed[0].name.replace(' and ', ' or ')}` : `game at ${listed.map((v) => v.name.replace(' and ', ' or ')).join(' or ')}`
    return `<p class="fine event">No ${esc(who)} during your stay. Concerts and other events put up temporary no-parking signs too, so read them.</p>`
  }
  // The Bowl and the Greek (shows, no feed), or a stadium whose schedule we couldn't read lately.
  const open = ev.venues.filter((x) => !x.listed && !(quiet && !x.teams))
  if (!open.length) return ''
  const games = open.some((x) => x.teams)
  return `<p class="warn">On ${games ? 'game and event days' : 'show nights'} at ${esc(andList(open.map((x) => x.name)))}, the streets around get temporary no-parking and tow-away signs. If there's ${games ? 'a game or show' : 'a show'}, read every sign.</p>`
}

/** "today", "tomorrow", "Sat", or "Nov 21" for a day further out. */
function dayWord(ms, now) {
  const d = local(ms).date, today = local(now).date
  if (d === today) return 'today'
  if (d === addDays(today, 1)) return 'tomorrow'
  const days = Math.round((Date.parse(d) - Date.parse(today)) / 86400000)
  if (days > 1 && days < 7) return ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][local(ms).dow]
  const [, m, dd] = d.split('-').map(Number)
  return `${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][m - 1]} ${dd}`
}

export function oddsCard(r, { title = 'Ticket patrol', win } = {}) {
  if (!r) return ''
  if (!r.covered) {
    const who = ENFORCER[r.enforcer]
    const text = r.enforcer === 'UCLA' ? 'No ticket odds for UCLA campus streets.'
      : r.enforcer === 'LAC' ? 'No ticket odds for unincorporated LA County.'
      : `No ticket odds for ${who ?? 'this city'} yet.`
    return `<section class="odds" aria-label="Ticket odds"><div class="odds-head"><span class="odds-title">${title}</span></div>
      <p class="muted">${esc(text)}</p></section>`
  }
  const word = { low: 'Unlikely', some: 'Possible', likely: 'Likely', very: 'Very likely' }[r.level]
  const stay = win ? span(win.end - win.start) : 'stay'
  const peak = r.peak && r.peak.p >= 0.05 ? ` The busiest hour of your stay is ${timesLabel(local(r.peak.ms).min, local(r.peak.ms).min + 60)}.` : ''
  const mix = r.mix.length ? ` Usually for ${andList(r.mix.map((m) => m.label))}.` : ''
  const holiday = r.holiday ? `<p class="fine">Holiday: enforcement is usually much lighter today.</p>` : ''
  return `<section class="odds lvl-${r.level}" aria-label="Ticket odds">
    <div class="odds-head"><span class="odds-title">${title}</span><span class="odds-word">${word}${r.scope === 'street' ? '' : ' on this block'}</span></div>
    <p class="odds-line"><b>${pctOf(r.pBlock)}%</b> chance an officer is ticketing on this block during your ${esc(stay)}${r.scope === 'street' ? ' (estimate)' : ''}.</p>
    <p class="odds-area">${pctOf(r.p)}% ${r.scope === 'street' ? `somewhere on ${esc(r.street)} in West Hollywood` : 'within two blocks'}</p>
    ${spark(r, win)}
    <p class="muted">Each bar is an hour ${local(r.dayStart).date === local(Date.now()).date ? 'today' : `on ${['Mondays', 'Tuesdays', 'Wednesdays', 'Thursdays', 'Fridays', 'Saturdays', 'Sundays'][local(r.dayStart + HOUR).dow]}`}.${peak}${mix} Parked legally, an officer passing by is nothing to worry about.</p>${holiday}
  </section>`
}

// 24 bars for the first day of the stay, with the stay highlighted. SVG attributes, not styles (CSP).
function spark(r, win) {
  if (!r.day?.length) return ''
  const W = 240, H = 44, bw = W / 24
  const max = Math.max(0.25, ...r.day)
  const inStay = (h) => {
    if (!win) return false
    const a = r.dayStart + h * HOUR, b = a + HOUR
    return b > win.start && a < win.end
  }
  const bars = r.day.map((p, h) => {
    const bh = Math.max(1.5, (p / max) * (H - 12))
    return `<rect x="${(h * bw + 1).toFixed(1)}" y="${(H - 12 - bh).toFixed(1)}" width="${(bw - 2).toFixed(1)}" height="${bh.toFixed(1)}" rx="1.5" class="${inStay(h) ? 'on' : ''}"/>`
  }).join('')
  const ticks = [[0, '12a'], [6, '6a'], [12, '12p'], [18, '6p']].map(([h, t]) => `<text x="${h * bw + 1}" y="${H - 1}">${t}</text>`).join('')
  return `<svg class="spark" viewBox="0 0 ${W} ${H}" role="img" aria-label="Ticketing by hour today">${bars}${ticks}</svg>`
}

// The next 24 hours on this curb: free / paid / no parking, with your stay bracketed. SVG
// attributes only (CSP). Tick labels every 6 hours of local time.
/** The strip's heading: "Next 24 hours", or "24 hours from 6pm" for a later arrival. */
export const stripLabel = (day) => (day?.length && day[0].from > Date.now() + 15 * MIN ? `24 hours from ${when(day[0].from, Date.now())}` : 'Next 24 hours')

export function dayStrip(day, win) {
  if (!day?.length) return ''
  const t0 = day[0].from, span = day[day.length - 1].to - t0, W = 300, H = 34
  const x = (ms) => Math.max(0, Math.min(W, ((ms - t0) / span) * W))
  const segs = day.map((d) => `<rect x="${x(d.from).toFixed(1)}" y="6" width="${Math.max(0.5, x(d.to) - x(d.from)).toFixed(1)}" height="12" class="k-${d.kind}"/>`).join('')
  const stay = win ? `<rect x="${x(win.start).toFixed(1)}" y="2" width="${Math.max(1, x(win.end) - x(win.start)).toFixed(1)}" height="20" class="stay" rx="2"/>` : ''
  const ticks = []
  const first = Math.ceil(t0 / HOUR) * HOUR
  for (let t = first; t < t0 + span; t += HOUR) {
    const m = local(t).min
    if (m % 360 === 0) ticks.push(`<text x="${x(t).toFixed(1)}" y="${H - 2}">${m === 0 ? '12a' : m === 720 ? '12p' : clock(m).replace(/:00/, '').replace('am', 'a').replace('pm', 'p')}</text>`)
  }
  return `<svg class="strip" viewBox="0 0 ${W} ${H}" role="img" aria-label="When this curb is free, paid or off-limits over 24 hours">${segs}${stay}${ticks.join('')}</svg>
    <p class="strip-key"><span><i class="k k-free"></i>Free</span><span><i class="k k-paid"></i>Meter</span><span><i class="k k-no"></i>No parking</span><span><i class="k k-stay"></i>Your stay</span></p>`
}

const pctOf = (p) => { const n = Math.round(p * 100); return n < 1 ? '<1' : String(n) }

/**
 * Ticket odds on a curb's sheet: the chance for your stay, and one bar per hour under the 24-hour
 * strip (same time axis), so "no parking 7-9am" sits above the hours officers work. Tap or arrow
 * through the bars for one hour's odds (wireCurbOdds).
 */
export function curbOdds(r, rh, day, win) {
  if (!r) return ''
  if (!r.covered) return oddsCard(r, { title: 'Ticket odds here', win })
  const word = { low: 'Unlikely', some: 'Possible', likely: 'Likely', very: 'Very likely' }[r.level]
  const street = r.scope === 'street'
  let bars = ''
  if (rh && day?.length) {
    const t0 = day[0].from, spanMs = day[day.length - 1].to - t0, W = 300, H = 50, base = H - 12
    const x = (ms) => Math.max(0, Math.min(W, ((ms - t0) / spanMs) * W))
    const max = Math.max(0.25, ...rh.block)
    const inStay = (a) => a + HOUR > win.start && a < win.end
    const rects = rh.block.map((p, h) => {
      const a = rh.t0 + h * HOUR, x0 = x(a), x1 = x(a + HOUR)
      if (x1 - x0 < 1) return ''
      const bh = Math.max(1.5, (p / max) * (base - 4))
      return `<rect data-h="${h}" x="${(x0 + 1).toFixed(1)}" y="${(base - bh).toFixed(1)}" width="${Math.max(0.5, x1 - x0 - 2).toFixed(1)}" height="${bh.toFixed(1)}" rx="1.5" class="${inStay(a) ? 'on' : ''}"/>`
    }).join('')
    const ticks = []
    for (let t = Math.ceil(t0 / HOUR) * HOUR; t < t0 + spanMs; t += HOUR) {
      const m = local(t).min
      if (m % 360 === 0) ticks.push(`<text x="${x(t).toFixed(1)}" y="${H - 1}">${m === 0 ? '12a' : m === 720 ? '12p' : clock(m).replace(/:00/, '').replace('am', 'a').replace('pm', 'p')}</text>`)
    }
    const data = esc(JSON.stringify({ t0: rh.t0, block: rh.block, area: rh.area, dom: rh.dom, street }))
    bars = `<svg class="spark obars" viewBox="0 0 ${W} ${H}" tabindex="0" role="img" aria-label="Ticketing on this block by hour, over the same 24 hours. Use the arrow keys to read each hour." data-odds="${data}"><rect class="sel-band" x="0" y="0" width="0" height="${base}"/>${rects}${ticks.join('')}</svg>
      <p class="odds-hour" aria-live="polite"></p>`
  }
  const mix = r.mix.length ? ` Mostly ${andList(r.mix.map((m) => m.label))}.` : ''
  return `<section class="odds odds-sheet lvl-${r.level}" aria-label="Ticket odds on this curb">
    <div class="odds-head"><span class="odds-title">Ticket patrol on this block</span><span class="odds-word">${word}</span></div>
    <p class="odds-line"><b>${pctOf(r.pBlock)}%</b> chance an officer is ticketing ${street ? 'on this block (estimate)' : 'here'} during your ${esc(span(win.end - win.start))} stay.</p>
    ${bars}
    <p class="muted">${mix ? mix.trim() + ' ' : ''}Parked legally and paid up, an officer walking by is nothing to worry about. It's what you risk if the meter runs out or you stay too long.</p>
  </section>`
}

/** Bars → "3–4pm today: about an 18% chance an officer tickets here". Defaults to your arrival hour. */
export function wireCurbOdds(root, win) {
  const svg = root.querySelector('.obars')
  if (!svg) return
  const d = JSON.parse(svg.dataset.odds)
  const out = svg.nextElementSibling
  const rects = [...svg.querySelectorAll('rect[data-h]')]
  const hours = rects.map((r) => Number(r.dataset.h))
  const band = svg.querySelector('.sel-band')
  let cur = hours.find((h) => d.t0 + (h + 1) * HOUR > win.start) ?? hours[0]
  const show = (h) => {
    cur = h
    const a = d.t0 + h * HOUR
    rects.forEach((r) => r.classList.toggle('sel', Number(r.dataset.h) === h))
    const r = rects.find((x) => Number(x.dataset.h) === h)
    if (r && band) { band.setAttribute('x', String(Number(r.getAttribute('x')) - 1)); band.setAttribute('width', String(Number(r.getAttribute('width')) + 2)) }
    const what = d.dom[h] ? ` Mostly ${CAT_LABEL[d.dom[h]] ?? d.dom[h]} then.` : ''
    const area = d.street ? '' : ` (within two blocks, ${d.area[h] < 0.01 ? 'under 1%' : `${Math.round(d.area[h] * 100)}%`})`
    const share = (p) => (p < 0.01 ? 'under a 1%' : `about a ${Math.round(p * 100)}%`)
    out.textContent = `${timesLabel(local(a).min, local(a).min + 60)} ${dayName(a)}: ${share(d.block[h])} chance an officer tickets ${d.street ? 'on this block' : 'here'}${area}.${what}`
  }
  const pick = (ev) => {
    const b = svg.getBoundingClientRect()
    const vx = ((ev.clientX - b.left) / b.width) * 300
    let best = cur, bd = Infinity
    for (const r of rects) {
      const c = Number(r.getAttribute('x')) + Number(r.getAttribute('width')) / 2
      if (Math.abs(c - vx) < bd) { bd = Math.abs(c - vx); best = Number(r.dataset.h) }
    }
    show(best)
  }
  svg.addEventListener('click', pick)
  svg.addEventListener('keydown', (e) => {
    const i = hours.indexOf(cur)
    if (e.key === 'ArrowRight' && i < hours.length - 1) { show(hours[i + 1]); e.preventDefault() }
    if (e.key === 'ArrowLeft' && i > 0) { show(hours[i - 1]); e.preventDefault() }
  })
  show(cur)
}

/** "today", "tomorrow" or the weekday, for one hour's readout. */
const dayName = (ms) => {
  const d = local(ms).date, today = local(Date.now()).date
  return d === today ? 'today' : d === addDays(today, 1) ? 'tomorrow' : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][local(ms).dow]
}

/** "today 10am", "tomorrow 8am", "Tue 10am", "Nov 17 10am": when the next sweep is. */
const dayAt = (ms, now = Date.now()) => {
  const d = local(ms).date, t = local(now).date, c = clock(local(ms).min)
  return d === t ? `today ${c}` : d === addDays(t, 1) ? `tomorrow ${c}` : when(ms, now)
}

/**
 * Street cleaning on a curb (curbDetail's sweep / sweepSkip / sweepArea): the posted schedule with
 * its weeks and the next date, a sweep the stay skips (off week, holiday), or with no sweeping sign
 * in our data, the city's route schedule for the area. Within 36 hours it's a warning.
 */
export function sweepLine(d) {
  const skip = d.sweepSkip ? `<p class="sweep sweep-off">${esc(d.sweepSkip)}.</p>` : ''
  const s = d.sweep
  if (s) {
    const soon = s.start - Date.now() < 36 * HOUR
    return `${skip}<p class="sweep${soon ? ' soon' : ''}"><b>Street cleaning</b> ${esc(s.days)}, ${esc(s.times)}. Next: <b>${esc(dayAt(s.start))}</b>.</p>`
  }
  const a = d.sweepArea
  if (a?.length) return `${skip}${areaSweep(a, 'Check for a sweeping sign on your side.')}`
  return skip
}

/** The posted routes around a point: "Street sweeping here: 1st & 3rd Mon 8–10am and …". */
export function areaSweep(a, tail = 'Check the sign on your side.') {
  if (!a?.length) return ''
  const soon = a[0].start - Date.now() < 36 * HOUR
  const list = andList(a.map((x) => `${x.days} ${x.times}`))
  const sides = a.length > 1 ? ', usually one side of the street each' : ''
  return `<p class="sweep${soon ? ' soon' : ''}"><b>Street sweeping here</b>: ${esc(list)}${sides}. Next: <b>${esc(dayAt(a[0].start))}</b>. ${esc(tail)}</p>`
}

export function signLines(lines) {
  return `<div class="sign">${lines.map((l) => `<div class="sign-row tone-${l.tone}"><span class="sign-when">${esc(l.days)} <span>${esc(l.times)}</span></span><span class="sign-what">${esc(l.what)}</span></div>`).join('')}</div>`
}

export function sheet(html) {
  $('sheetBody').innerHTML = html
  if (!$('sheet').open) $('sheet').showModal()
  $('sheet').scrollTop = 0
  // Safari focuses the first control (the ×) and rings it; start on the heading instead. A second
  // call (details replacing "Loading…") replaces that heading, so keep focus inside the sheet.
  const a = document.activeElement
  if (a?.classList.contains('close') || !$('sheet').contains(a)) {
    const h = $('sheetBody').querySelector('h1, h2')
    if (h) { h.tabIndex = -1; h.focus({ preventScroll: true }) } else $('sheet').querySelector('.close')?.focus({ preventScroll: true })
  }
}

let barObserver = null

export function setBar(html) {
  let bar = $('bar')
  if (!html) {
    barObserver?.disconnect()
    barObserver = null
    bar?.remove()
    document.body.classList.remove('with-bar')
    document.documentElement.style.removeProperty('--bar-h')
    return null
  }
  if (!bar) {
    bar = document.createElement('div')
    bar.id = 'bar'
    bar.className = 'actionbar'
    bar.setAttribute('role', 'region')
    bar.setAttribute('aria-label', 'Actions')
    document.body.append(bar)
    // The page ends just above the bar, however tall this screen's bar is (CSS --bar-h).
    if (typeof ResizeObserver === 'function') {
      // Applied on the next frame, and only when it changed: setting it inside the callback makes
      // Safari report a "ResizeObserver loop" error.
      barObserver = new ResizeObserver(() => requestAnimationFrame(() => {
        const h = `${Math.ceil(bar.offsetHeight)}px`
        if (bar.isConnected && document.documentElement.style.getPropertyValue('--bar-h') !== h) document.documentElement.style.setProperty('--bar-h', h)
      }))
      barObserver.observe(bar)
    }
  }
  bar.className = 'actionbar' // a new screen's bar starts plain (drive mode's "you're here" ring goes)
  bar.innerHTML = html
  document.body.classList.add('with-bar')
  return bar
}

export function busy(button, text) {
  const label = button.innerHTML
  button.disabled = true
  button.innerHTML = `<span class="spinner"></span> ${esc(text)}`
  return () => { button.disabled = false; button.innerHTML = label }
}


export function driveText(a, b) {
  const m = Math.hypot((a.lat - b.lat) * 111320, (a.lon - b.lon) * 92300) * 1.35
  return m < 160 ? 'a block' : `${(m / 1609).toFixed(1)} mi`
}

export const short = (name) => (name.length > 28 ? name.slice(0, 26).trimEnd() + '…' : name)

// The sheet and drive card already say when to move; the notes add everything else.
export function notesList(notes, max = 9) {
  const rest = notes.filter((n) => !n.startsWith('Move by')).slice(0, max)
  return rest.length ? `<ul class="notes">${rest.map((n) => `<li${/meters? (open|taken) right now/.test(n) ? ' id="liveNote"' : ''}>${esc(n)}</li>`).join('')}</ul>` : ''
}
